<template>
  <!-- These section wrappers deliberately carry NO bottom padding. They used to have `pb-6`, which
       stacked under the trailing divider's own `my-10` and left the divider sitting 40px below the
       content above it but 64px above the section below — asymmetric, and almost certainly not
       intended. Removing it also gives back the ~24px that a module head needs to fit in what is
       left of a page instead of opening a new one: it took the stress fixture from 17 pages to 16
       and emptied three near-blank pages. -->
  <div>
    <executive-summary-module-section
      :view-model="viewModels.cloudSecurity"
      :show-zero-values="showZeroValues"
    >
      <template #before>
        <report-section-banner
          class="mb-10"
          :title="$t('reports.executiveSummaryReport.protection')"
        />
      </template>
      <template #extra>
        <executive-summary-bot-attacks-card v-if="botAttacks" :bot-attacks="botAttacks" />
      </template>
    </executive-summary-module-section>
    <v-divider class="border-opacity-100 my-10" color="indigo-faint" />
  </div>

  <div>
    <executive-summary-module-section
      :view-model="viewModels.emailSecurity"
      :show-zero-values="showZeroValues"
      :tickets-by-type-title="
        emailSecurity ? $t('reports.executiveSummaryReport.remainingTicketsByType') : undefined
      "
      :tickets-by-type-sub-title="
        emailSecurity
          ? $t('reports.executiveSummaryReport.remainingTicketsCount', {
              n: formatReportNumber(emailSecurity.remainingTicketsTotal),
            })
          : undefined
      "
    >
      <template #extra>
        <div v-if="emailSecurity" class="executive-summary-protection__email-extras mt-10">
          <div class="executive-summary-protection__email-extras-row page-break-avoid">
            <executive-summary-allow-block-list
              :list-type="ExecutiveSummaryEmailListType.ALLOWLIST"
              :counts="emailSecurity.allowlist"
            />
            <executive-summary-allow-block-list
              :list-type="ExecutiveSummaryEmailListType.BLOCKLIST"
              :counts="emailSecurity.blocklist"
            />
          </div>
          <div class="executive-summary-protection__email-extras-row page-break-avoid">
            <report-metric-tile
              :value="emailSecurity.warningOnly.totalEvents"
              :label="$t('reports.executiveSummaryReport.warningOnlyEmailEvents')"
              :icon="WARNING_ONLY_TILE_ICON"
            />
            <report-metric-tile
              :value="emailSecurity.warningOnly.percentOfInboundEmail"
              :format="ReportMetricValueFormat.PERCENT"
              :label="$t('reports.executiveSummaryReport.warningEventsPercentOfInbound')"
              :icon="WARNING_ONLY_TILE_ICON"
            />
          </div>
        </div>
      </template>
    </executive-summary-module-section>
    <v-divider class="border-opacity-100 my-10" color="indigo-faint" />
  </div>

  <div v-for="module in plainModules" :key="module">
    <executive-summary-module-section
      :view-model="viewModels[module]"
      :show-zero-values="showZeroValues"
    />
    <v-divider class="border-opacity-100 my-10" color="indigo-faint" />
  </div>

  <executive-summary-security-awareness-section
    :data="securityAwarenessData"
    :show-zero-values="showZeroValues"
  />
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import ReportSectionBanner from "@/components/reports/ReportSectionBanner.vue";
import ReportMetricTile from "@/components/reports/ReportMetricTile.vue";
import ExecutiveSummaryModuleSection from "@/components/reports/executive-summary/ExecutiveSummaryModuleSection.vue";
import ExecutiveSummaryBotAttacksCard from "@/components/reports/executive-summary/ExecutiveSummaryBotAttacksCard.vue";
import ExecutiveSummaryAllowBlockList from "@/components/reports/executive-summary/ExecutiveSummaryAllowBlockList.vue";
import ExecutiveSummarySecurityAwarenessSection from "@/components/reports/executive-summary/ExecutiveSummarySecurityAwarenessSection.vue";
import {
  EXECUTIVE_SUMMARY_MODULE_ORDER,
  ExecutiveSummaryEmailListType,
  ReportMetricValueFormat,
} from "@/constants/reports";
import { CoroIcons } from "@/constants/coro-icon";
import { SubscriptionModule } from "@/constants/workplaces";
import type { TicketGeneratingSubscriptionModule } from "@/constants/workplaces";
import { formatReportNumber } from "@/_helpers/reports";
import { adaptExecutiveSummaryModule } from "@/_store/reports/adapters";
import type { ExecutiveSummaryModuleViewModel } from "@/_store/reports/adapters";
import type {
  ExecutiveSummaryEmailListCounts,
  ExecutiveSummaryReportData,
} from "@/_store/reports.module";

// Stands in for an absent allow/block list: ExecutiveSummaryAllowBlockList takes `counts` as a
// required prop and reads three fields straight off it, so `undefined` would throw inside it.
const EMPTY_EMAIL_LIST_COUNTS: ExecutiveSummaryEmailListCounts = { emails: 0, domains: 0, ips: 0 };

// The mock draws the warning-only tiles with the suspicious-email-content glyph, not the generic
// email one they used to carry. Built here because the value is constant — no prop or store value
// can change it.
const WARNING_ONLY_TILE_ICON = `$${CoroIcons.SUSPICIOUS_EMAIL_CONTENT_S}`;

// Cloud and Email are rendered explicitly above because they fill the `extra` slot;
// the remaining four are structurally identical.
const PLAIN_MODULES = EXECUTIVE_SUMMARY_MODULE_ORDER.filter(
  (module) =>
    module !== SubscriptionModule.CLOUD_SECURITY && module !== SubscriptionModule.EMAIL_SECURITY
);

export default defineComponent({
  components: {
    ReportSectionBanner,
    ReportMetricTile,
    ExecutiveSummaryModuleSection,
    ExecutiveSummaryBotAttacksCard,
    ExecutiveSummaryAllowBlockList,
    ExecutiveSummarySecurityAwarenessSection,
  },
  props: {
    reportData: {
      type: Object as PropType<ExecutiveSummaryReportData>,
      required: true,
    },
    showZeroValues: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    // Written out as a complete record rather than built from EXECUTIVE_SUMMARY_MODULE_ORDER with
    // an `as` cast. The cast promised completeness the compiler never checked, so shortening that
    // array — the one edit a single ordering constant exists to make safe — would have fed
    // `undefined` into a `required` view-model prop and thrown at render. Here a missing key is a
    // compile error, and the array is left to do only what it is for: ordering.
    const viewModels = computed<
      Record<TicketGeneratingSubscriptionModule, ExecutiveSummaryModuleViewModel>
    >(() => {
      const adapt = (module: TicketGeneratingSubscriptionModule) =>
        adaptExecutiveSummaryModule(props.reportData, module);

      return {
        [SubscriptionModule.CLOUD_SECURITY]: adapt(SubscriptionModule.CLOUD_SECURITY),
        [SubscriptionModule.EMAIL_SECURITY]: adapt(SubscriptionModule.EMAIL_SECURITY),
        [SubscriptionModule.ENDPOINT_SECURITY]: adapt(SubscriptionModule.ENDPOINT_SECURITY),
        [SubscriptionModule.USER_DATA_GOVERNANCE]: adapt(SubscriptionModule.USER_DATA_GOVERNANCE),
        [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: adapt(
          SubscriptionModule.ENDPOINT_DATA_GOVERNANCE
        ),
        [SubscriptionModule.EDR]: adapt(SubscriptionModule.EDR),
      };
    });

    const botAttacks = computed(
      () => props.reportData.protection?.[SubscriptionModule.CLOUD_SECURITY]?.botAttacks
    );

    /**
     * Email Security's section with its newer sub-objects zero-filled.
     *
     * `remainingTicketsTotal`, `allowlist`, `blocklist` and `warningOnly` are all recent backend
     * additions that the template reads one and two levels into, and `v-if="emailSecurity"` proves
     * only that the section itself arrived. Filling them here rather than guarding at four call
     * sites also settles an inconsistency: a missing `warningOnly` THREW and blanked the whole
     * card, while a missing `remainingTicketsTotal` degraded quietly to "(0 tickets)" — one
     * payload produced both a crash and a wrong heading.
     */
    const emailSecurity = computed(() => {
      const section = props.reportData.protection?.[SubscriptionModule.EMAIL_SECURITY];
      if (!section) {
        return undefined;
      }
      return {
        ...section,
        remainingTicketsTotal: section.remainingTicketsTotal ?? 0,
        allowlist: section.allowlist ?? EMPTY_EMAIL_LIST_COUNTS,
        blocklist: section.blocklist ?? EMPTY_EMAIL_LIST_COUNTS,
        warningOnly: {
          totalEvents: section.warningOnly?.totalEvents ?? 0,
          percentOfInboundEmail: section.warningOnly?.percentOfInboundEmail ?? 0,
        },
      };
    });

    const securityAwarenessData = computed(
      () => props.reportData[SubscriptionModule.SECURITY_AWARENESS_TRAINING]
    );

    return {
      viewModels,
      botAttacks,
      emailSecurity,
      plainModules: PLAIN_MODULES,
      securityAwarenessData,
      formatReportNumber,
      ExecutiveSummaryEmailListType,
      ReportMetricValueFormat,
      WARNING_ONLY_TILE_ICON,
    };
  },
});
</script>

<style scoped lang="scss">
// Rows in a block container, each its own page-break unit — the same shape as
// ExecutiveSummaryImpactGrid, and for the same two reasons.
//
// The avoid cannot sit on an individual box: html2pdf pushes an element to the next page by
// inserting a spacer div before it, and a spacer inserted into a grid becomes a grid item, so it
// consumes a cell instead of displacing the flow. But putting ONE avoid around all four boxes —
// which is what this used to do — makes the whole 2x2 block indivisible, so whenever it does not
// fit in what is left of the page it moves entirely, and the page ends on a half-empty slab even
// though the allow/block row alone had room. A row is the smallest unit that is safe to keep
// whole and still small enough to place, so the break now lands between the two rows.
.executive-summary-protection__email-extras {
  &-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;

    &:not(:last-child) {
      margin-bottom: 16px;
    }
  }
}
</style>
